// ConfirmContext - a promise-based, styled confirm dialog that replaces the
// jarring native window.confirm() for destructive actions. useConfirm()
// returns a confirm() resolving true/false, so call sites stay a near
// drop-in: `if (!(await confirm({ title, body, danger: true }))) return;`.
// Built on the shared <Modal> (scrim, fade+slide, Esc, scroll-lock, a11y).

import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { Modal } from '@/components/ui';

interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  // Destructive actions get the red confirm button + treatment.
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  return ctx;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((o) => {
    setOpts(o);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = (result: boolean) => {
    resolver.current?.(result);
    resolver.current = null;
    setOpts(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal open={!!opts} onClose={() => settle(false)} title={opts?.title} size="sm">
        {opts?.body && (
          <p className="text-sm text-ink-300 leading-relaxed mb-5">{opts.body}</p>
        )}
        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={() => settle(false)}>
            {opts?.cancelLabel ?? 'Cancel'}
          </button>
          <button
            className={opts?.danger ? 'btn-danger' : 'btn-primary'}
            onClick={() => settle(true)}
            data-autofocus
          >
            {opts?.confirmLabel ?? 'Confirm'}
          </button>
        </div>
      </Modal>
    </ConfirmContext.Provider>
  );
}
